/**
 * Thread notification diff (ticket 3531, upstream pin e3e7cc3f, MIT).
 * Adapted from the transition body of upstream's
 * `ThreadNotificationCoordinator.tsx`, pulled out as a pure module so the
 * attention/completion bookkeeping can be tested without a shell. The data
 * source differs: upstream reads thread shells from the environment shell,
 * forge reads `SidebarThreadSummary` rows from the app store, so attention
 * states come from the forge status pill resolver instead of upstream's
 * `resolveSidebarThreadStatus`. The event semantics are upstream's: an
 * attention transition fires once per turn, a completion fires once per
 * completedAt timestamp, archived threads stay silent, first sight is a
 * silent baseline.
 *
 * @module threadNotificationCoordinator.logic
 */
import type { ThreadId } from "@cmmd-forge/contracts";

import type { SidebarThreadSummary } from "../types";
import { resolveThreadStatusPill, type ThreadStatusPill } from "./Sidebar.logic";

/** The slice of the sidebar thread summary the coordinator watches. */
export type SidebarThreadLike = Pick<
  SidebarThreadSummary,
  | "id"
  | "environmentId"
  | "title"
  | "archivedAt"
  | "latestTurn"
  | "session"
  | "status"
  | "interactionMode"
  | "hasPendingApprovals"
  | "hasPendingUserInput"
  | "hasActionableProposedPlan"
>;

export type NotificationAttentionStatus = "approval" | "failed" | "input";

export type ThreadNotificationEvent = {
  readonly threadId: ThreadId;
  readonly kind: "completion" | "input";
  readonly status: NotificationAttentionStatus | "ready";
  readonly title: string;
};

export type ThreadNotificationState = {
  readonly attention: string | null;
  readonly completion: number | null;
};

const ATTENTION_STATUS: Partial<Record<ThreadStatusPill["label"], NotificationAttentionStatus>> = {
  Failed: "failed",
  "Pending Approval": "approval",
  "Awaiting Input": "input",
};

export type ThreadWatch = {
  readonly title: string;
  readonly attention: string | null;
  readonly attentionStatus: NotificationAttentionStatus | null;
  readonly completedAt: number | null;
};

/**
 * One thread's notification-relevant state: the pill resolver decides
 * attention and completion, keyed so an unchanged state never refires.
 */
export function watchInputForThread(thread: SidebarThreadLike): ThreadWatch {
  const pill = resolveThreadStatusPill({ thread });
  const attentionStatus = pill ? (ATTENTION_STATUS[pill.label] ?? null) : null;
  const attention = attentionStatus
    ? `${thread.latestTurn?.turnId ?? ""}:${attentionStatus}`
    : null;
  const parsed =
    pill?.label === "Completed" && thread.latestTurn?.completedAt
      ? Date.parse(thread.latestTurn.completedAt)
      : null;
  return {
    title: thread.title,
    attention,
    attentionStatus,
    completedAt: parsed !== null && Number.isFinite(parsed) ? parsed : null,
  };
}

/**
 * Advance the watch map over one poll. Returns the events to announce and
 * the state map to carry into the next poll.
 */
export function diffThreadNotifications(
  previous: ReadonlyMap<ThreadId, ThreadNotificationState>,
  threads: ReadonlyArray<SidebarThreadLike>,
): { events: ThreadNotificationEvent[]; next: Map<ThreadId, ThreadNotificationState> } {
  const next = new Map<ThreadId, ThreadNotificationState>();
  const events: ThreadNotificationEvent[] = [];
  for (const thread of threads) {
    const prior = previous.get(thread.id);
    const watch = watchInputForThread(thread);
    const completion = watch.completedAt ?? prior?.completion ?? null;
    next.set(thread.id, { attention: watch.attention, completion });
    if (!prior || thread.archivedAt !== null) continue;
    if (watch.attentionStatus && watch.attention !== prior.attention) {
      events.push({
        threadId: thread.id,
        kind: "input",
        status: watch.attentionStatus,
        title: watch.title,
      });
      continue;
    }
    if (completion !== null && (prior.completion === null || completion > prior.completion)) {
      events.push({ threadId: thread.id, kind: "completion", status: "ready", title: watch.title });
    }
  }
  return { events, next };
}

/** Toast tone for an event, matching upstream's completion/failure/attention split. */
/**
 * The attention title for an event, shared by the toast and the desktop
 * notification so both surfaces read identically.
 */
export function notificationTitleForEvent(event: ThreadNotificationEvent): string {
  if (event.kind === "completion") return "Thread completed";
  if (event.status === "approval") return "Approval needed";
  if (event.status === "failed") return "Thread failed";
  return "Input needed";
}

export function toastTypeForEvent(event: ThreadNotificationEvent): "success" | "error" | "warning" {
  if (event.kind === "completion") return "success";
  if (event.status === "failed") return "error";
  return "warning";
}
